/**
 * MapPreview — design-time route to visualise the world map without
 * spinning up a server session. Mounts MapView with a synthetic GameState
 * so every continent is revealed and territories show owners + cubes.
 *
 * Reachable at `/map-preview?players=6` (or 2..6).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  asPlayerId,
  asSessionId,
  type GameState,
  type Player,
  type PlayerColorKey,
  type TerritoryRuntimeState,
} from '@qd/protocol';
import { TERRITORIES, type PlayerCount } from '@qd/map';
import { fontStacks, surfaces } from '@qd/design-tokens';
import { SlateBackground } from '../components/base/SlateBackground';
import { MapView } from '../components/map/MapView';
import { PlayerRoster } from '../components/play';

const COLORS: readonly PlayerColorKey[] = [
  'prussian',
  'vermillion',
  'mustard',
  'verdigris',
  'tyrian',
  'bone',
];

function buildPreviewState(playerCount: PlayerCount): GameState {
  const players: Player[] = Array.from({ length: playerCount }).map((_, i) => ({
    id: asPlayerId(`preview-p${i}`),
    name: `Player ${i + 1}`,
    color: COLORS[i % COLORS.length],
    isAdmin: i === 0,
    isConnected: true,
    reserveCubes: 0,
    dashboard: { tier1: [], tier2: [], tier3: [] } as never,
    hand: [],
    eliminated: false,
  }));

  // Deterministic PRNG so the preview looks identical between reloads.
  let s = 1337;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };

  const territories: Record<string, TerritoryRuntimeState> = {};
  for (const t of TERRITORIES) {
    const ownerIdx = Math.floor(rand() * playerCount);
    territories[t.id] = {
      territoryId: t.id,
      controllingPlayerId: players[ownerIdx]?.id ?? null,
      cubeCount: 1 + Math.floor(rand() * 6),
      hasEnvReadiness: false,
      fortifiedThisRound: false,
    };
  }

  return {
    sessionId: asSessionId('preview'),
    outerPhase: 'playing',
    phase: 'execute-tests',
    currentPlayerIdx: 0,
    turnNumber: 1,
    roundNumber: 1,
    releaseDeadlineTriggered: false,
    winnerId: null,
    playerOrder: players.map((p) => p.id),
    players,
    territories,
    modules: {},
    decks: {
      questions: { draw: [], discard: [] },
      events: { draw: [], discard: [] },
      documentation: { draw: [], discard: [] },
    },
    attackState: { kind: 'idle' },
    eventLog: [],
    learningAnswers: {},
    seed: 'preview',
    actionCount: 0,
    seq: 0,
    serverTime: Date.now(),
  };
}

export function MapPreview(): ReactNode {
  const [search] = useSearchParams();
  const requested = Number(search.get('players')) as PlayerCount;
  const [playerCount, setPlayerCount] = useState<PlayerCount>(
    requested >= 2 && requested <= 6 ? requested : 6,
  );

  const gameState = useMemo(() => buildPreviewState(playerCount), [playerCount]);
  const selfId = gameState.players[0]?.id ?? null;

  return (
    <SlateBackground particles={false}>
      <div
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 5,
          display: 'flex',
          gap: 6,
          background: 'rgba(245,236,211,0.92)',
          padding: '6px 10px',
          border: `1px solid ${surfaces.chalk}`,
          borderRadius: 2,
          fontFamily: fontStacks.mono,
          color: surfaces.chalk,
          fontSize: 10,
          letterSpacing: '0.16em',
          alignItems: 'center',
        }}
      >
        <span>PLAYERS</span>
        {([2, 3, 4, 5, 6] as const).map((n) => (
          <button
            key={n}
            onClick={() => setPlayerCount(n)}
            style={{
              background: n === playerCount ? surfaces.chalk : 'transparent',
              color: n === playerCount ? surfaces.slate : surfaces.chalk,
              border: `1px solid ${surfaces.chalk}`,
              padding: '2px 8px',
              fontFamily: fontStacks.mono,
              fontSize: 10,
              cursor: 'pointer',
              letterSpacing: '0.16em',
            }}
          >
            {n}
          </button>
        ))}
      </div>
      <div style={{ position: 'absolute', inset: 0 }}>
        <MapView
          playerCount={playerCount}
          gameState={gameState}
          selfId={selfId}
          onSelectSource={() => undefined}
          onSelectTarget={() => undefined}
        />
        <PlayerRoster gameState={gameState} selfId={selfId} />
      </div>
    </SlateBackground>
  );
}
